import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList } from '@fortawesome/pro-solid-svg-icons/faClipboardList';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons/faEllipsis';

import { formatDateRange } from 'us-react-components/dist/utils/date';
import { InterestedSitter, InterestedSitterService, Job, JobService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { CloseJobModal, CloseJobStatus } from '../JobDetail/components/CloseJobModal';

interface JobWithStatus extends Job {
  numberOfApplicants?: number;
  interestedSitters?: InterestedSitter[];
}

export const Posts = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [isOpenJobs, setIsOpenJobs] = useState(true);
  const [openJobs, setOpenJobs] = useState<JobWithStatus[]>([]);
  const [closedJobs, setClosedJobs] = useState<JobWithStatus[]>([]);
  const [totalOpen, setTotalOpen] = useState(0);
  const [totalClosed, setTotalClosed] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const jobPostedParam = searchParams.get('jobPosted');
  const [jobPostedDismissed, setJobPostedDismissed] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [closeModalJob, setCloseModalJob] = useState<JobWithStatus | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (openMenuId == null) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return (): void => document.removeEventListener('mousedown', onDocClick);
  }, [openMenuId]);

  const submitCloseJob = async (status: CloseJobStatus): Promise<void> => {
    if (!closeModalJob?.id || !user?.id) return;
    await JobService.patch(
      closeModalJob.id,
      { id: closeModalJob.id, status } as Job,
      { params: { userId: user.id } },
    );
    toast.success(t('pages.posts.closedToast'));
    setCloseModalJob(null);
    setOpenJobs((prev) => prev.filter((j) => j.id !== closeModalJob.id));
    setTotalOpen((n) => Math.max(0, n - 1));
  };

  useEffect(() => {
    document.title = 'UrbanSitter - Your Posted Jobs';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setIsLoading(true);

    const hydrateInterest = async (
      list: JobWithStatus[],
      setter: (jobs: JobWithStatus[]) => void,
    ): Promise<void> => {
      const enriched = await Promise.all(
        list.map(async (job) => {
          if (!job.id || !(job.numberOfApplicants ?? 0)) return job;
          try {
            const resp = await InterestedSitterService.getByFilter({
              params: { jobId: job.id, userId: user.id, parentId: user.id, withdrawn: false },
            });
            return { ...job, interestedSitters: (resp.data?.data ?? []) as InterestedSitter[] };
          } catch {
            return job;
          }
        }),
      );
      if (!cancelled) setter(enriched);
    };

    const loadBoth = async (): Promise<void> => {
      const baseParams = {
        userId: user.id,
        parentId: user.id,
        sort: 'updated',
        limit: 20,
      };
      try {
        const [openResp, closedResp] = await Promise.all([
          JobService.getByFilter({ params: { ...baseParams, status: 'open' } }),
          JobService.getByFilter({ params: { ...baseParams, status: 'closed' } }),
        ]);
        if (cancelled) return;
        const openList = (openResp.data?.data ?? []) as JobWithStatus[];
        const closedList = (closedResp.data?.data ?? []) as JobWithStatus[];
        setOpenJobs(openList);
        setClosedJobs(closedList);
        setTotalOpen(openResp.data?.meta?.size ?? 0);
        setTotalClosed(closedResp.data?.meta?.size ?? 0);
        hydrateInterest(openList, setOpenJobs);
        hydrateInterest(closedList, setClosedJobs);
      } catch (err) {
        console.warn('[Posts] load failed', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadBoth();
    return (): void => {
      cancelled = true;
    };
  }, [user?.id]);

  const jobs = isOpenJobs ? openJobs : closedJobs;
  const isNullState = !isLoading && jobs.length === 0;
  const notifyTitle =
    jobPostedParam === '3'
      ? t('pages.posts.postedHeadingRecurring')
      : t('pages.posts.postedHeadingOneTime');

  return (
    <div>
      <div className='bg-neutral-100 border-b border-neutral-300'>
        <div className='container'>
          <ul className='flex m-0 py-2'>
            <li className='mx-1'>
              <Button
                buttonType={ButtonType.tertiary}
                id='open-jobs-tab'
                className={`cursor-pointer px-3 py-2 inline-block ${
                  isOpenJobs ? 'font-semibold border-b-2 border-sapphire' : ''
                }`}
                onClick={(): void => setIsOpenJobs(true)}
              >
                {t('pages.posts.openTab', { count: totalOpen })}
              </Button>
            </li>
            <li className='mx-1'>
              <Button
                buttonType={ButtonType.tertiary}
                id='closed-jobs-tab'
                className={`cursor-pointer px-3 py-2 inline-block ${
                  !isOpenJobs ? 'font-semibold border-b-2 border-sapphire' : ''
                }`}
                onClick={(): void => setIsOpenJobs(false)}
              >
                {t('pages.posts.closedTab', { count: totalClosed })}
              </Button>
            </li>
          </ul>
        </div>
      </div>

      {isLoading && (
        <div className='text-center py-8'>
          <i className='fa-solid fa-spinner fa-spin mr-2' />
          <span>{t('pages.posts.loading')}</span>
        </div>
      )}

      {!isLoading && (
        <div className='container py-5'>
          {jobPostedParam && !jobPostedDismissed && (
            <div className='bg-emerald-50 border border-emerald-200 mb-4 py-4 px-4 rounded'>
              <Button
                buttonType={ButtonType.tertiary}
                aria-label='Dismiss notification'
                id='close-success'
                className='float-right text-2xl leading-none'
                onClick={(): void => setJobPostedDismissed(true)}
              >
                ×
              </Button>
              <h4 className='mb-0 font-semibold'>{notifyTitle}</h4>
              <p className='my-2'>
                {t('pages.posts.postedBody')}{' '}
                {jobPostedParam === '3' && openJobs.length > 0 && (
                  <>
                    {t('pages.posts.postedEditScreening')}{' '}
                    <a href={`/app/job/${openJobs[0].id}/edit`} className='underline'>
                      {t('pages.posts.postedEditScreeningLink')}
                    </a>
                    ?
                  </>
                )}
              </p>
            </div>
          )}

          {isOpenJobs && openJobs.length > 0 && (
            <h3 className='text-lg italic font-light mb-4 hidden sm:block'>
              {t('pages.posts.subheading')}
            </h3>
          )}

          {isNullState && (
            <div className='max-w-lg mx-auto text-center'>
              {isOpenJobs ? (
                <>
                  <img
                    src='https://assets.urbansitter.com/static/img/emptystateposts.png'
                    alt=''
                    className='mb-4 mx-auto'
                  />
                  <h1 className='font-medium mb-2'>{t('pages.posts.emptyPostHeading')}</h1>
                  <h3 className='font-light'>{t('pages.posts.emptyPostBody')}</h3>
                </>
              ) : (
                <h1 className='font-light mt-4'>{t('pages.posts.emptyClosedHeading')}</h1>
              )}
              <div className='text-center mt-4'>
                <Button
                  id='post-a-job'
                  buttonType={ButtonType.primary}
                  onClick={(): void => navigate('/job-post')}
                  className='px-8'
                >
                  {t('pages.posts.postAJob')}
                </Button>
              </div>
            </div>
          )}

          {!isNullState && (
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 items-start'>
              {isOpenJobs && openJobs.length > 0 && (
                <div
                  id='post-new-job-from-card'
                  className='us-card rounded-lg border border-neutral-200 shadow-sm bg-white text-center p-6'
                >
                  <FontAwesomeIcon icon={faClipboardList} className='block text-5xl mb-3 text-sapphire mx-auto' />
                  <h2 className='font-light'>
                    {t('pages.posts.postNewCardLine1')}
                    <br />
                    {t('pages.posts.postNewCardLine2')}
                  </h2>
                  <hr className='my-4' />
                  <Button
                    id='post-new-job-cta'
                    buttonType={ButtonType.primary}
                    onClick={(): void => navigate('/job-post')}
                    className='px-8'
                  >
                    + {t('pages.posts.postNewCardCta')}
                  </Button>
                </div>
              )}
              {jobs.map((job) => {
                const applicantCount = job.numberOfApplicants ?? 0;
                const thumbs = (job.interestedSitters ?? []).slice(0, 5);
                const status = job.status ?? '';
                const isJobOpen = status === 'open' || status === 'new';
                const isMenuOpen = openMenuId === job.id;
                return (
                  <div
                    key={job.id}
                    className='us-card rounded-lg border border-neutral-200 shadow-sm bg-white p-4 cursor-pointer transition hover:border-sapphire hover:shadow-md relative'
                    onClick={(): void => navigate(`/job/${job.id}`)}
                    onKeyDown={(e): void => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/job/${job.id}`);
                      }
                    }}
                    role='button'
                    tabIndex={0}
                  >
                    <div className='absolute top-2 right-2' ref={isMenuOpen ? menuRef : undefined}>
                      <Button
                        id={`open-overflow-menu-${job.id}`}
                        buttonType={ButtonType.tertiary}
                        aria-label={t('pages.posts.moreOptionsAriaLabel')}
                        className='!p-1 !min-w-0'
                        onClick={(e): void => {
                          e.stopPropagation();
                          setOpenMenuId(isMenuOpen ? null : job.id ?? null);
                        }}
                      >
                        <FontAwesomeIcon icon={faEllipsis} className='text-xl text-neutral-600' />
                      </Button>
                      {isMenuOpen && (
                        <ul
                          role='menu'
                          className='absolute right-0 top-8 z-10 bg-white shadow rounded border border-neutral-200 py-1 w-48 text-sm text-left'
                          onClick={(e): void => e.stopPropagation()}
                          onKeyDown={(e): void => e.stopPropagation()}
                        >
                          <li>
                            <button
                              id={`view-post-details-${job.id}`}
                              type='button'
                              className='block w-full text-left px-4 py-2 hover:bg-neutral-100'
                              onClick={(): void => {
                                setOpenMenuId(null);
                                navigate(`/job/${job.id}`);
                              }}
                            >
                              {t('pages.posts.menu.viewPostDetails')}
                            </button>
                          </li>
                          <li>
                            <button
                              id={`clone-post-${job.id}`}
                              type='button'
                              className='block w-full text-left px-4 py-2 hover:bg-neutral-100'
                              onClick={(): void => {
                                setOpenMenuId(null);
                                navigate(`/job-post?presetId=${job.id}`);
                              }}
                            >
                              {t('pages.posts.menu.cloneJob')}
                            </button>
                          </li>
                          {isJobOpen && (
                            <>
                              <li>
                                <button
                                  id={`edit-job-${job.id}`}
                                  type='button'
                                  className='block w-full text-left px-4 py-2 hover:bg-neutral-100'
                                  onClick={(): void => {
                                    setOpenMenuId(null);
                                    navigate(`/job/${job.id}/edit`);
                                  }}
                                >
                                  {t('pages.posts.menu.editJob')}
                                </button>
                              </li>
                              <li>
                                <button
                                  id={`close-job-${job.id}`}
                                  type='button'
                                  className='block w-full text-left px-4 py-2 hover:bg-neutral-100'
                                  onClick={(): void => {
                                    setOpenMenuId(null);
                                    setCloseModalJob(job);
                                  }}
                                >
                                  {t('pages.posts.menu.closeJob')}
                                </button>
                              </li>
                            </>
                          )}
                        </ul>
                      )}
                    </div>
                    <div className='font-semibold pr-8'>
                      {job.recurring
                        ? `${job.availability ?? ''}, Starting ${formatDateRange(job.booking?.start)}`
                        : formatDateRange(job.booking?.start, job.booking?.end)}
                    </div>
                    <div className='text-sm'>
                      {job.booking?.rate ? `$${job.booking.rate}/hr` : t('pages.sitters.nullHeading')}
                    </div>
                    <div className='text-sm mb-2'>{job.booking?.address?.city}</div>
                    {thumbs.length > 0 && (
                      <div className='flex items-center gap-1 my-2'>
                        {thumbs.map((interest) => (
                          <img
                            key={interest.sitter?.id}
                            src={interest.sitter?.pictureUrl}
                            alt={interest.sitter?.name ?? interest.sitter?.firstName ?? ''}
                            className='w-10 h-10 rounded-full object-cover border border-neutral-200'
                          />
                        ))}
                      </div>
                    )}
                    <Button
                      buttonType={ButtonType.secondary}
                      onClick={(e): void => {
                        e.stopPropagation();
                        navigate(`/job/${job.id}/interest`);
                      }}
                      className='w-full mt-2'
                    >
                      View ({applicantCount} interested caregiver{applicantCount === 1 ? '' : 's'})
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <CloseJobModal
            isOpen={closeModalJob !== null}
            onClose={(): void => setCloseModalJob(null)}
            onSubmit={submitCloseJob}
          />
        </div>
      )}
    </div>
  );
});
